import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  Controller,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Helmet } from "react-helmet-async";

import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";
import FormProvider from "../../../components/hook-form";
import { useSettingsContext } from "../../../components/settings";
import { PATH_DASHBOARD } from "../../../routes/paths";
import { useAppContext } from "../../../providers/AppContext";
import { IDispenseRequest } from "../../../@types/dispense";
import { ClinicalRecords } from "../../../api/clinicalRecords.service";
import { DispenseService } from "../../../api/dispense.service";
import { enqueueSnackbar } from "../../../components/snackbar";
import useBarcodeScanner from "../../../hooks/useBarcodeScanner";
import { ProductService } from "../../../api/product.service";
import { ProductType } from "../../../@types/product";
import {
  TILL_BARCODE_NAV_STATE_KEY,
  activeProductToDispenseLine,
  findProductByBarcode,
} from "./tillBarcodeShared";
import { buildTillFocusState } from "./tillNavShared";
import FramesAndLenses from "../Dispense/Specs/FramesAndLenses";
import AddonAndVoucher from "../Dispense/Specs/AddonAndVoucher";
import { Addon } from "../../../@types/addon.type";
import { Vouchers } from "../../../@types/voucher.type";
import {
  FinalSummaryReadOnly,
  pickLatestFinalFromRecords,
} from "../Patient/Refraction/FinalSummary";

const DISPENSE_OTHER_PRODUCTS = "dispense_other_products";

const SIGHT_TEST_AGE = [
  { value: "under_60", label: "Less than 60" },
  { value: "over_60", label: "Greater than 60" },
] as const;

const SIGHT_TEST_DIOPTERS = [
  { value: "short_sighted", label: "Short-sightedness" },
  { value: "long_sighted", label: "Long-sightedness" },
] as const;

const SIGHT_TEST_COUNTRY = [
  { value: "scotland", label: "Scotland" },
  { value: "england", label: "England" },
] as const;

function getDispenseSubmitErrorMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: unknown; error?: string } };
    message?: string;
  };
  const data = e?.response?.data;
  if (data) {
    const m = data.message;
    if (Array.isArray(m)) {
      const parts = m
        .filter((x) => x != null && String(x).trim())
        .map(String);
      if (parts.length) return parts.join(" ");
    }
    if (typeof m === "string" && m.trim()) return m.trim();
    if (typeof data.error === "string" && data.error.trim()) {
      return data.error.trim();
    }
  }
  if (typeof e?.message === "string" && e.message.trim()) {
    return e.message.trim();
  }
  return "Error creating dispense";
}

function splitGuestName(full: string): { firstName: string; surName?: string } {
  const t = full.trim();
  if (!t) return { firstName: "" };
  const parts = t.split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], surName: parts.slice(1).join(" ") };
}

const toMoney = (value: unknown): number => {
  const parsed =
    value === null || value === undefined || value === ""
      ? 0
      : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

function isValidEmail(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

function isValidPhone(value: string): boolean {
  const t = value.trim();
  if (!t) return false;
  const digits = t.replace(/\D/g, "");
  return digits.length >= 6;
}

type TillRetailMode = "external" | "existing";

type Props = { mode: TillRetailMode };

function isBatchAwareLine(item: Record<string, unknown>): boolean {
  if (item.requiresLotTracking === true) return true;
  const t = String(item.type ?? "");
  return t === ProductType.CONTACT_LENSES || t === ProductType.SOLUTION;
}

export default function TillRetailSalePage({ mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStretch } = useSettingsContext();
  const { patients, getPatients } = useAppContext();

  const [addons, setAddons] = useState<Addon[]>([]);
  const [vouchers, setVouchers] = useState<Vouchers[]>([]);
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const manualDiscount = 0;
  const [submitting, setSubmitting] = useState(false);
  const [pickupErrors, setPickupErrors] = useState<{
    date?: string;
    time?: string;
  }>({});

  const [guestFullName, setGuestFullName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [existingPhone, setExistingPhone] = useState("");
  const [existingEmail, setExistingEmail] = useState("");
  const [patientRefractionFinal, setPatientRefractionFinal] = useState<
    Record<string, unknown> | null
  >(null);
  const [refractionFinalLoading, setRefractionFinalLoading] = useState(false);
  const [sightTestAge, setSightTestAge] = useState("");
  const [sightTestDiopters, setSightTestDiopters] = useState("");
  const [sightTestCountry, setSightTestCountry] = useState("");
  const [sightTestErrors, setSightTestErrors] = useState<{
    age?: string;
    diopters?: string;
    country?: string;
  }>({});
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const barcodeNavHandledRef = useRef(false);

  const methods = useForm<IDispenseRequest>({
    mode: "onChange",
    defaultValues: {
      patientId: null,
      type: DISPENSE_OTHER_PRODUCTS,
      specType: "",
      pdRight: null,
      pdLeft: null,
      heightRight: null,
      heightLeft: null,
      bvd: null,
      pantoscopicTilt: null,
      wrapAngle: null,
      adjustedPrice: 0,
      totalPrice: 0,
      discount: 0,
      pickupDateTime: "",
      framesAndLenses: [],
      addons: [],
      vouchers: [],
      orderingNotes: "",
      handoverNotes: "",
      hobbies: "",
      isVDU: false,
      isSmoker: false,
      isDriver: false,
      isExternal: mode === "external",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "framesAndLenses",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await new ProductService().getActiveProducts();
        if (!cancelled) {
          setAllProducts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Failed to fetch products for till barcode scanner", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleBarcodeScan = useCallback(
    (barcode: string) => {
      const product = findProductByBarcode(allProducts, barcode);
      if (product) {
        (append as any)(activeProductToDispenseLine(product));
        enqueueSnackbar(`Added ${product.name}`, { variant: "success" });
      } else {
        enqueueSnackbar(`Product with code "${barcode}" not found`, {
          variant: "warning",
        });
      }
    },
    [allProducts, append],
  );

  useBarcodeScanner({ onScan: handleBarcodeScan });

  useEffect(() => {
    const state = location.state as Record<string, unknown> | null;
    const payload = state?.[TILL_BARCODE_NAV_STATE_KEY];
    if (!payload || typeof payload !== "object") {
      barcodeNavHandledRef.current = false;
      return;
    }
    if (barcodeNavHandledRef.current) {
      return;
    }
    barcodeNavHandledRef.current = true;
    const line = payload as ReturnType<typeof activeProductToDispenseLine>;
    (append as any)({
      id: line.id,
      name: line.name,
      type: line.type,
      price: line.price,
      category: line.category,
      code: line.code,
      quantity: line.quantity ?? 1,
      differentRL: line.differentRL ?? false,
    });
    enqueueSnackbar(`Added ${line.name}`, { variant: "success" });
    const { [TILL_BARCODE_NAV_STATE_KEY]: _drop, ...rest } = state || {};
    navigate(location.pathname, {
      replace: true,
      state: Object.keys(rest).length ? rest : {},
    });
  }, [location.state, location.pathname, append, navigate]);

  const watchedFramesAndLenses = useWatch({ control, name: "framesAndLenses" });
  const watchedAdjusted = useWatch({ control, name: "adjustedPrice" });
  const watchedTotal = useWatch({ control, name: "totalPrice" });
  const watchedDiscount = useWatch({ control, name: "discount" });
  const watchedVoucherRows = useWatch({ control, name: "vouchers" });
  const watchedPatientId = useWatch({ control, name: "patientId" });

  const voucherDiscountTotal = useMemo(() => {
    if (!watchedVoucherRows?.length || !vouchers.length) return 0;
    return watchedVoucherRows.reduce((sum: number, row: { voucher?: unknown }) => {
      const vid = row?.voucher;
      if (vid === "" || vid == null) return sum;
      const v = vouchers.find((x) => String(x.id) === String(vid));
      return sum + (v ? Number(v.price ?? 0) : 0);
    }, 0);
  }, [watchedVoucherRows, vouchers]);

  const manualDiscountTotal = Math.max(
    0,
    toMoney(watchedDiscount) - voucherDiscountTotal,
  );

  const framesAndLensesPrice = useMemo(() => {
    if (!watchedFramesAndLenses?.length) return 0;
    return watchedFramesAndLenses.reduce((total: number, item: any) => {
      const price = parseFloat(item.price) || 0;
      const qty = isBatchAwareLine(item)
        ? Math.max(1, Number(item.quantityIssued ?? item.quantity) || 1)
        : Math.max(1, Number(item.quantity) || 1);
      return total + price * qty;
    }, 0);
  }, [watchedFramesAndLenses]);

  useEffect(() => {
    if (mode === "existing") {
      getPatients();
    }
  }, [mode, getPatients]);

  useEffect(() => {
    if (mode !== "existing") {
      return;
    }
    if (watchedPatientId == null || watchedPatientId === "") {
      setExistingPhone("");
      setExistingEmail("");
      return;
    }
    const p = patients.find(
      (x) => String(x.id) === String(watchedPatientId),
    );
    if (p) {
      setExistingPhone((p.phone || "").trim());
      setExistingEmail((p.email || "").trim());
    }
  }, [mode, watchedPatientId, patients]);

  useEffect(() => {
    if (mode !== "existing") {
      setPatientRefractionFinal(null);
      setRefractionFinalLoading(false);
      return;
    }
    if (watchedPatientId == null || watchedPatientId === "") {
      setPatientRefractionFinal(null);
      setRefractionFinalLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setRefractionFinalLoading(true);
      try {
        const res = await new ClinicalRecords().getAllClinicalRecords(
          String(watchedPatientId),
        );
        const list = Array.isArray(res.data) ? res.data : [];
        const latest = pickLatestFinalFromRecords(list);
        if (!cancelled) {
          setPatientRefractionFinal(latest);
        }
      } catch {
        if (!cancelled) {
          setPatientRefractionFinal(null);
        }
      } finally {
        if (!cancelled) {
          setRefractionFinalLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, watchedPatientId]);

  const setDateWithClear = (d: Date | null) => {
    setPickupErrors((e) => ({ ...e, date: undefined }));
    setDate(d);
  };

  const setTimeWithClear = (t: string | null) => {
    setPickupErrors((e) => ({ ...e, time: undefined }));
    setTime(t);
  };

  const title = mode === "external" ? "External Sale" : "Create New Sale";

  const onSubmit = async (data: IDispenseRequest) => {
    let patientId: string | number | null = data.patientId;

    if (mode === "external") {
      const name = guestFullName.trim();
      if (!name) {
        enqueueSnackbar("Patient name is required", { variant: "error" });
        return;
      }
      if (!splitGuestName(name).firstName) {
        enqueueSnackbar("Please enter a valid patient name", {
          variant: "error",
        });
        return;
      }
      if (!isValidPhone(guestPhone)) {
        enqueueSnackbar(
          "Phone is required (at least 6 digits)",
          { variant: "error" },
        );
        return;
      }
      if (!isValidEmail(guestEmail)) {
        enqueueSnackbar("A valid email address is required", {
          variant: "error",
        });
        return;
      }
      const stErr: { age?: string; diopters?: string; country?: string } = {};
      if (!sightTestAge) {
        stErr.age = "Required";
      }
      if (!sightTestDiopters) {
        stErr.diopters = "Required";
      }
      if (!sightTestCountry) {
        stErr.country = "Required";
      }
      if (stErr.age || stErr.diopters || stErr.country) {
        setSightTestErrors(stErr);
        enqueueSnackbar("Please complete all Sight Test fields", {
          variant: "error",
        });
        return;
      }
      setSightTestErrors({});
    } else if (!patientId) {
      enqueueSnackbar("Please select a patient", { variant: "error" });
      return;
    } else {
      if (!isValidPhone(existingPhone)) {
        enqueueSnackbar(
          "Phone is required (at least 6 digits)",
          { variant: "error" },
        );
        return;
      }
      if (!isValidEmail(existingEmail)) {
        enqueueSnackbar("A valid email address is required", {
          variant: "error",
        });
        return;
      }
    }

    const nextPickup: { date?: string; time?: string } = {};
    if (!date) {
      nextPickup.date = "Pickup date is required";
    }
    if (time == null || String(time).trim() === "") {
      nextPickup.time = "Pickup time is required";
    }
    if (nextPickup.date || nextPickup.time) {
      setPickupErrors(nextPickup);
      enqueueSnackbar("Pickup date and time are required", {
        variant: "error",
      });
      return;
    }
    setPickupErrors({});

    if (!data.framesAndLenses?.length) {
      enqueueSnackbar("Please add at least one product", { variant: "error" });
      return;
    }

    for (let i = 0; i < data.framesAndLenses.length; i++) {
      const item = data.framesAndLenses[i] as any;
      if (!item.name || !item.type || item.price == null || !item.category) {
        enqueueSnackbar(`Please complete product line ${i + 1}`, {
          variant: "error",
        });
        return;
      }
      if (item.id == null || item.id === "") {
        enqueueSnackbar(
          `Product line ${i + 1} must be added from the product picker`,
          { variant: "error" },
        );
        return;
      }
      const needsLot = isBatchAwareLine(item);
      if (needsLot) {
        const hasLot = !!(item.goodsInId && String(item.goodsInId).trim());
        if (!hasLot) {
          enqueueSnackbar(
            `Select a received lot for line ${i + 1} (lot-tracked product).`,
            { variant: "error" },
          );
          return;
        }
        const packSize = Math.max(
          1,
          Math.floor(Number(item.packSize ?? 1) || 1),
        );
        const quantityIssued = Math.max(
          1,
          Math.floor(Number(item.quantityIssued ?? item.quantity ?? 1) || 1),
        );
        if (!packSize || !quantityIssued) {
          enqueueSnackbar(
            `Pack size and quantity issued are required for line ${i + 1}.`,
            { variant: "error" },
          );
          return;
        }
      }
    }

    const currentDiscount = toMoney(methods.getValues("discount"));
    const currentTotal = toMoney(methods.getValues("totalPrice"));
    if (currentDiscount > currentTotal) {
      enqueueSnackbar("Discount cannot exceed the total price", {
        variant: "error",
      });
      return;
    }

    setSubmitting(true);
    try {
      const framesAndLensesPayload = data.framesAndLenses
        .map((item: any) => {
          const quantity = Math.max(1, Number(item.quantity) || 1);
          const isLotTracked = isBatchAwareLine(item);
          const packSize = isLotTracked
            ? Math.max(1, Math.floor(Number(item.packSize ?? 1) || 1))
            : undefined;
          const quantityIssued = isLotTracked
            ? Math.max(
                1,
                Math.floor(Number(item.quantityIssued ?? quantity) || 1),
              )
            : undefined;
          return {
            productId: String(item.id),
            quantity: isLotTracked ? packSize! * quantityIssued! : quantity,
            packSize,
            quantityIssued,
            goodsInId: item.goodsInId
              ? String(item.goodsInId).trim() || undefined
              : undefined,
          };
        })
        .filter((row: { productId: string }) => !!row.productId);

      const addonIds = data.addons
        .map((item: any) => Number(item.addon))
        .filter((id: number) => !isNaN(id) && id !== 0);

      const voucherIds = data.vouchers
        .map((item: any) => Number(item.voucher))
        .filter((id: number) => !isNaN(id) && id !== 0);

      const pickupDateTime =
        date && time
          ? new Date(
              `${date.toISOString().split("T")[0]}T${time}:00`,
            ).toISOString()
          : "";

      const nameParts = splitGuestName(guestFullName.trim());
      const dispensePart = {
        status: "draft",
        type: DISPENSE_OTHER_PRODUCTS,
        specType: "",
        framesAndLenses: framesAndLensesPayload,
        addons: addonIds,
        vouchers: voucherIds,
        pickupDateTime,
        orderingNotes: data.orderingNotes?.trim() || undefined,
        handoverNotes: data.handoverNotes?.trim() || undefined,
        hobbies: data.hobbies?.trim() || undefined,
        isExternal: mode === "external",
        isVDU: data.isVDU,
        isSmoker: data.isSmoker,
        isDriver: data.isDriver,
        adjustedPrice: roundMoney(toMoney(data.adjustedPrice)),
        totalPrice: roundMoney(toMoney(data.totalPrice)),
        discount: roundMoney(toMoney(data.discount)),
        pdRight: null,
        pdLeft: null,
        heightRight: null,
        heightLeft: null,
        bvd: null,
        pantoscopicTilt: null,
        wrapAngle: null,
      };

      const checkoutBody: Record<string, unknown> = {
        mode: mode === "external" ? "external" : "existing",
        receiptNo: receiptNo.trim() || undefined,
        dispense: dispensePart,
      };

      if (mode === "existing") {
        checkoutBody.patientId = String(patientId);
      } else {
        checkoutBody.externalPatient = {
          firstName: nameParts.firstName,
          surName: nameParts.surName || undefined,
          phone: guestPhone.trim() || undefined,
          email: guestEmail.trim() || undefined,
        };
        checkoutBody.sightTest = {
          ageBand: sightTestAge,
          diopters: sightTestDiopters,
          country: sightTestCountry,
        };
      }

      const response = await new DispenseService().retailTillCheckout(
        checkoutBody,
      );

      enqueueSnackbar("Sale created. Redirecting to Till…", {
        variant: "success",
      });

      const tillId = response?.data?.till?.id;
      if (tillId) {
        navigate(`/till/${tillId}`, {
          state: buildTillFocusState(response?.data?.dispense?.id),
        });
      } else {
        enqueueSnackbar("Till was not returned; opening Till list", {
          variant: "warning",
        });
        navigate(PATH_DASHBOARD.till.list);
      }
    } catch (err: unknown) {
      console.error(err);
      enqueueSnackbar(getDispenseSubmitErrorMessage(err), {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container maxWidth={themeStretch ? false : "xl"}>
        <CustomBreadcrumbs
          heading={title}
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Till", href: PATH_DASHBOARD.till.list },
            { name: title },
          ]}
        />

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <Typography variant="h6" sx={{ mb: 2, px: 1 }}>
                Customer
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 3,
                  px: 1,
                }}
              >
                {mode === "external" ? (
                  <>
                    <TextField
                      label="Patient name"
                      value={guestFullName}
                      onChange={(e) => setGuestFullName(e.target.value)}
                      required
                      sx={{ flex: "1 1 240px", minWidth: 200 }}
                    />
                    <TextField
                      label="Receipt no."
                      value={receiptNo}
                      onChange={(e) => setReceiptNo(e.target.value)}
                      placeholder="Optional"
                      sx={{ flex: "1 1 180px", minWidth: 160 }}
                    />
                    <TextField
                      label="Phone"
                      value={guestPhone}
                      onChange={(e) => setGuestPhone(e.target.value)}
                      required
                      sx={{ flex: "1 1 200px", minWidth: 180 }}
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                      sx={{ flex: "1 1 240px", minWidth: 200 }}
                    />
                  </>
                ) : (
                  <>
                    <Controller
                      name="patientId"
                      control={control}
                      rules={{ required: "Patient is required" }}
                      render={({ field }) => (
                        <TextField
                          select
                          label="Patient"
                          {...field}
                          value={field.value ?? ""}
                          error={!!errors.patientId}
                          helperText={errors.patientId?.message as string}
                          required
                          sx={{ flex: "1 1 280px", minWidth: 220 }}
                        >
                          <MenuItem value="">
                            <em>Select patient</em>
                          </MenuItem>
                          {patients.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <TextField
                      label="Receipt no."
                      value={receiptNo}
                      onChange={(e) => setReceiptNo(e.target.value)}
                      placeholder="Optional"
                      sx={{ flex: "1 1 180px", minWidth: 160 }}
                    />
                    <TextField
                      label="Phone"
                      value={existingPhone}
                      onChange={(e) => setExistingPhone(e.target.value)}
                      required
                      sx={{ flex: "1 1 200px", minWidth: 180 }}
                    />
                    <TextField
                      label="Email"
                      type="email"
                      value={existingEmail}
                      onChange={(e) => setExistingEmail(e.target.value)}
                      required
                      sx={{ flex: "1 1 240px", minWidth: 200 }}
                    />
                  </>
                )}
              </Box>

              {mode === "external" && (
                <Box
                  sx={{
                    px: 1,
                    mb: 3,
                    py: 2,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    borderRadius: 1,
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Sight Test{" "}
                    {/* <Box component="span" sx={{ color: "error.main" }}>
                      *
                    </Box> */}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <TextField
                      select
                      label="Age"
                      value={sightTestAge}
                      onChange={(e) => {
                        setSightTestAge(e.target.value);
                        setSightTestErrors((x) => ({ ...x, age: undefined }));
                      }}
                      required
                      error={!!sightTestErrors.age}
                      helperText={sightTestErrors.age}
                      sx={{ flex: "1 1 200px", minWidth: 180 }}
                    >
                      <MenuItem value="">
                        <em>Select age</em>
                      </MenuItem>
                      {SIGHT_TEST_AGE.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Diopters"
                      value={sightTestDiopters}
                      onChange={(e) => {
                        setSightTestDiopters(e.target.value);
                        setSightTestErrors((x) => ({
                          ...x,
                          diopters: undefined,
                        }));
                      }}
                      required
                      error={!!sightTestErrors.diopters}
                      helperText={sightTestErrors.diopters}
                      sx={{ flex: "1 1 200px", minWidth: 180 }}
                    >
                      <MenuItem value="">
                        <em>Select diopters</em>
                      </MenuItem>
                      {SIGHT_TEST_DIOPTERS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Country"
                      value={sightTestCountry}
                      onChange={(e) => {
                        setSightTestCountry(e.target.value);
                        setSightTestErrors((x) => ({
                          ...x,
                          country: undefined,
                        }));
                      }}
                      required
                      error={!!sightTestErrors.country}
                      helperText={sightTestErrors.country}
                      sx={{ flex: "1 1 200px", minWidth: 180 }}
                    >
                      <MenuItem value="">
                        <em>Select country</em>
                      </MenuItem>
                      {SIGHT_TEST_COUNTRY.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              )}

              {mode === "existing" && (
                <Box sx={{ px: 1, mb: 2 }}>
                  <FinalSummaryReadOnly
                    finalData={patientRefractionFinal}
                    loading={refractionFinalLoading}
                    showPlaceholder={
                      watchedPatientId == null || watchedPatientId === ""
                    }
                  />
                </Box>
              )}

              {/* <Typography variant="h6" sx={{ mb: 1, px: 1 }}>
                Products{" "}
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </Typography> */}
              <FramesAndLenses
                specType=""
                dispenseMode={DISPENSE_OTHER_PRODUCTS}
                fields={fields}
                append={append}
                remove={remove}
                disabled={false}
              />

              <AddonAndVoucher
                control={control}
                addons={addons}
                vouchers={vouchers}
                setAddons={setAddons}
                setVouchers={setVouchers}
                disabled={false}
                date={date}
                setDate={setDateWithClear}
                time={time}
                setTime={setTimeWithClear}
                framesAndLensesPrice={framesAndLensesPrice}
                initialManualDiscount={manualDiscount}
                pickupDateError={pickupErrors.date}
                pickupTimeError={pickupErrors.time}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 3,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => navigate(PATH_DASHBOARD.till.list)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={submitting}
                >
                  Save &amp; open Till
                </LoadingButton>
              </Box>
            </Box>

            <RetailOrderSidebar
              lines={watchedFramesAndLenses as any[]}
              adjustedPrice={toMoney(watchedAdjusted)}
              totalPrice={toMoney(watchedTotal)}
              voucherDiscount={voucherDiscountTotal}
              manualDiscount={manualDiscountTotal}
            />
          </Box>
        </FormProvider>
      </Container>
    </>
  );
}

function RetailOrderSidebar({
  lines,
  adjustedPrice,
  totalPrice,
  voucherDiscount,
  manualDiscount,
}: {
  lines: any[];
  adjustedPrice: number;
  totalPrice: number;
  voucherDiscount: number;
  manualDiscount: number;
}) {
  const subtotal = useMemo(() => {
    if (!lines?.length) return 0;
    return lines.reduce((s, row) => s + (parseFloat(row.price) || 0), 0);
  }, [lines]);

  return (
    <Box
      sx={(theme) => ({
        width: { xs: "100%", md: 320 },
        flexShrink: 0,
        border: `2px solid ${theme.palette.success.main}`,
        borderRadius: 2,
        p: 2,
        bgcolor:
          theme.palette.mode === "dark"
            ? theme.palette.background.paper
            : theme.palette.grey[50],
      })}
    >
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Your order
      </Typography>
      <Box sx={{ minHeight: 120, mb: 2 }}>
        {!lines?.length ? (
          <Typography variant="body2" color="text.secondary">
            Add products from the list on the left.
          </Typography>
        ) : (
          lines.map((row, i) => (
            <Box
              key={row.id ?? i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                py: 0.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ pr: 1 }}>
                {row.name}
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                £{roundMoney(toMoney(row.price)).toFixed(2)}
              </Typography>
            </Box>
          ))
        )}
      </Box>
      <SidebarRow label="Subtotal" value={subtotal} />
      {voucherDiscount > 0 && (
        <SidebarRow label="Voucher" value={-voucherDiscount} emphasize />
      )}
      {manualDiscount > 0 && (
        <SidebarRow label="Discount" value={-manualDiscount} emphasize />
      )}
      <SidebarRow label="Total" value={totalPrice} bold />
      <SidebarRow label="Amount to pay" value={adjustedPrice} bold />
      <SidebarRow label="Outstanding" value={adjustedPrice} bold warn />
    </Box>
  );
}

function SidebarRow({
  label,
  value,
  bold,
  emphasize,
  warn,
}: {
  label: string;
  value: number;
  bold?: boolean;
  emphasize?: boolean;
  warn?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        py: 0.5,
      }}
    >
      <Typography
        variant={bold ? "body1" : "body2"}
        fontWeight={bold ? 700 : 400}
        color={warn ? "error.main" : "text.primary"}
      >
        {label}
      </Typography>
      <Typography
        variant={bold ? "body1" : "body2"}
        fontWeight={bold ? 700 : emphasize ? 600 : 400}
        color={warn ? "error.main" : "text.primary"}
      >
        £{roundMoney(value).toFixed(2)}
      </Typography>
    </Box>
  );
}
